import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { LegalLinks } from "../components/LegalLinks";
import logo from "../assets/twu-logo.png";

const BG = "https://images.unsplash.com/photo-1527576539890-dfa815648363?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBhcmNoaXRlY3R1cmUlMjBibGFjayUyMGFuZCUyMHdoaXRlfGVufDB8fHx8MTc4NDU0OTIxMnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/");
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email });
        setSuccess(data.message);
      } else {
        const res = await register(name, email, password);
        setSuccess(res.message || "Registration received. Await admin approval.");
        setMode("login");
        setPassword("");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isLogin = mode === "login";
  const isForgot = mode === "forgot";
  const switchMode = (m) => { setMode(m); setError(""); setSuccess(""); };
  const eyebrow = isForgot ? "Account Recovery" : isLogin ? "Staff Portal" : "Rep Registration";
  const heading = isForgot ? "Forgot password" : isLogin ? "Sign in" : "Register";
  const blurb = isForgot ? "Enter your account email and we'll send you a link to reset your password."
    : isLogin ? "Access the grievance case management dashboard." : "Create a rep account. An admin will approve your access.";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-20 py-12">
        <div className="max-w-sm w-full animate-fade-up">
          <div className="flex items-center gap-3 mb-12">
            <img src={logo} alt="Transport Workers Union Local 555" className="w-28 h-auto" />
            <div>
              <div className="font-head font-extrabold text-base leading-none tracking-tight">TWU LOCAL 555 BNA</div>
              <div className="text-[10px] tracking-[0.3em] text-muted-foreground mt-1">GRIEVANCE TRACKER</div>
            </div>
          </div>

          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{eyebrow}</p>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">{heading}</h1>
          <p className="text-sm text-muted-foreground mb-8">{blurb}</p>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em]">Full Name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="register-name-input" className="rounded-sm h-11" required />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em]">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" className="rounded-sm h-11" required />
            </div>
            {!isForgot && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em]">Password</Label>
                  {isLogin && (
                    <button type="button" data-testid="forgot-password-link" onClick={() => switchMode("forgot")}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors">Forgot password?</button>
                  )}
                </div>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input" className="rounded-sm h-11" required />
              </div>
            )}
            {error && (
              <div data-testid="login-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-sm px-3 py-2">
                {error}
              </div>
            )}
            {success && (
              <div data-testid="register-success" className="text-sm text-green-700 border border-green-300 bg-green-50 rounded-sm px-3 py-2 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />{success}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid={isForgot ? "forgot-submit-button" : isLogin ? "login-submit-button" : "register-submit-button"}
              className="w-full h-11 rounded-sm bg-black hover:bg-neutral-800 text-white group">
              {loading ? "Please wait…" : isForgot ? "Send reset link" : isLogin ? "Sign in" : "Create account"}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>

          <button
            data-testid="toggle-auth-mode"
            onClick={() => switchMode(isLogin ? "register" : "login")}
            className="mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {isLogin ? "New rep? Register for access →" : "← Back to sign in"}
          </button>
        </div>
        <div className="max-w-sm w-full mt-12">
          <LegalLinks />
        </div>
      </div>
      <div className="hidden lg:block relative grain overflow-hidden bg-neutral-900">
        <img src={BG} alt="architecture" className="w-full h-full object-cover opacity-90" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-head text-2xl font-bold tracking-tight leading-snug">Track every case, from filing to resolution.</p>
        </div>
      </div>
    </div>
  );
}
